import type { Property, Unit } from './property'
import type { Profile } from './user'

export type TenancyStatus = 'onboarding' | 'moving_in' | 'active' | 'ending' | 'ended' | 'archived'
export type RentFrequency = 'weekly' | 'monthly'
export type DepositScheme = 'dps' | 'tds' | 'mydeposits' | 'other'
export type ContractType = 'ast' | 'periodic' | 'company_let' | 'licence_to_occupy' | 'assured_periodic'
export type RentReviewInterval = 'none' | 'every_6_months' | 'annually' | 'every_2_years'
export type RentReviewNoticePeriod = 'none' | '1_month' | '2_months' | '3_months'
export type TenancyApprovalStatus = 'draft' | 'approved'
export type DepositType = 'custodial' | 'insured'
export type TerminationReason = 'tenant_notice' | 'landlord_section8' | 'mutual_agreement' | 'abandonment'
export type TenantDocumentType = 'gas_safety' | 'eicr' | 'epc' | 'deposit_cert' | 'inventory' | 'ast' | 'other' | 'written_statement_of_terms' | 'government_information_sheet' | 'prescribed_information' | 'legionella' | 'hmo_licence' | 'buildings_insurance' | 'landlord_insurance' | 'fire_safety' | 'pat_testing'

export interface Tenancy {
  id: string
  property_id: string
  unit_id: string
  rent_amount_pence: number | null
  rent_frequency: RentFrequency
  rent_due_day: number
  start_date: string
  end_date: string | null
  notice_period_days: number
  break_clause_date: string | null
  break_clause_terms: string | null
  contract_type: ContractType
  rent_review_date: string | null
  rent_review_interval: RentReviewInterval | null
  rent_review_notice_period: RentReviewNoticePeriod | null
  guarantor_name: string | null
  guarantor_email: string | null
  guarantor_phone: string | null
  assigned_agent_id: string | null
  tenancy_status: TenancyApprovalStatus | null
  deposit_amount_pence: number | null
  deposit_scheme: DepositScheme | null
  deposit_scheme_other: string | null
  deposit_reference: string | null
  deposit_protected_date: string | null
  deposit_paid_at: string | null
  deposit_received_at: string | null
  deposit_acknowledged_at: string | null
  deposit_acknowledged_by: string | null
  deposit_type: DepositType | null
  prescribed_info_served_at: string | null
  prescribed_info_guarantor_served_at: string | null
  prescribed_info_certified_at: string | null
  prescribed_info_certified_by: string | null
  guarantor_address_line1: string | null
  guarantor_address_line2: string | null
  guarantor_address_city: string | null
  guarantor_address_postcode: string | null
  status: TenancyStatus
  arrears_alert_days: number
  // The 8 `ast_*` columns are deliberately absent. They are the superseded
  // agreement representation (Decision Record §5); all 42 live rows hold NULL
  // in every one of them, and a served agreement now lives in
  // `generated_agreements` + `tenant_documents`. The columns still EXIST in
  // the database — dropping them is a separate, deploy-ordered migration —
  // so nothing here breaks if a row still carries a value; the app simply
  // does not read or write them.
  is_pre_rra: boolean
  gis_required: boolean
  rent_last_increased_at: string | null
  written_statement_served_at: string | null
  government_info_sheet_served_at: string | null
  government_info_sheet_served_externally: boolean
  terminated_at: string | null
  termination_reason: TerminationReason | null
  notice_served_at: string | null
  notice_end_date: string | null
  created_at: string
  updated_at: string
  // Joined
  property?: Property
  unit?: Unit
  tenants?: TenancyTenant[]
  tenant_invites?: Array<{
    id: string
    full_name: string | null
    email: string | null
    phone: string | null
    status: string
  }>
}

export interface TenancyTenant {
  id: string
  tenancy_id: string
  tenant_id: string
  is_primary: boolean
  added_at: string
  removed_at: string | null
  // Joined
  tenant?: Pick<Profile, 'id' | 'full_name' | 'email' | 'phone'>
}

export interface TenantDocument {
  id: string
  tenancy_id: string
  tenant_id: string | null
  document_type: TenantDocumentType
  document_name: string
  /**
   * Where the file is: a path inside this row's bucket. The one name used for
   * this across every file-bearing table (migration 480).
   *
   * NULL for a compliance-linked served document, whose file lives in the
   * `compliance-documents` bucket and is reached through
   * `compliance_document_id` — 8 live rows, which previously held a stale
   * cross-bucket signed URL in the old `file_url` column.
   */
  storage_path: string | null
  /**
   * A RESOLVED signed URL, set by the service layer at read time. Never
   * persisted and never a path — the column it used to come from is gone in
   * migration 481.
   */
  file_url?: string | null
  compliance_document_id: string | null
  served_at: string
  viewed_at: string | null
  acknowledged_at: string | null
  acknowledgement_ip: string | null
  acknowledgement_user_agent: string | null
  created_by: string
  created_at: string
}

export interface DocumentReminder {
  id: string
  document_id: string
  tenant_id: string
  reminder_type: 'auto_48h' | 'manual'
  sent_at: string
  sent_by: string | null
  created_at: string
}

// ============================================================
// COMPLIANCE-LINKED DOCUMENT TYPES
// ============================================================

/**
 * Maps TenantDocumentType values that correspond to compliance certificates
 * to their ComplianceDocumentType equivalents. These types must be served
 * from existing property compliance records — not uploaded ad-hoc.
 */
export const COMPLIANCE_LINKED_TENANT_DOC_TYPES: Record<string, string> = {
  gas_safety: 'gas_safety',
  eicr: 'eicr',
  epc: 'epc',
  legionella: 'legionella',
  hmo_licence: 'hmo_licence',
  buildings_insurance: 'buildings_insurance',
  landlord_insurance: 'landlord_insurance',
  fire_safety: 'fire_safety',
  pat_testing: 'pat_testing',
}

export function isComplianceLinkedType(type: TenantDocumentType | string): boolean {
  return type in COMPLIANCE_LINKED_TENANT_DOC_TYPES
}

// Insert/Update types
export type TenancyInsert = Omit<Tenancy, 'id' | 'created_at' | 'updated_at' | 'property' | 'unit' | 'tenants'>
export type TenancyUpdate = Partial<TenancyInsert>

// Constants
export const CONTRACT_TYPES: { value: ContractType; label: string }[] = [
  { value: 'assured_periodic', label: 'Assured Periodic Tenancy' },
  { value: 'periodic', label: 'Periodic Tenancy' },
  { value: 'ast', label: 'Pre-RRA Tenancy' },
  { value: 'company_let', label: 'Company Let' },
  { value: 'licence_to_occupy', label: 'Licence to Occupy' },
]

export const isPeriodicTenancy = (contractType: ContractType | string): boolean => {
  return contractType === 'periodic' || contractType === 'assured_periodic'
}

export const TENANCY_APPROVAL_STATUSES: { value: TenancyApprovalStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
]

export const DEPOSIT_SCHEMES: { value: DepositScheme; label: string }[] = [
  { value: 'dps', label: 'Deposit Protection Service (DPS)' },
  { value: 'tds', label: 'Tenancy Deposit Scheme (TDS)' },
  { value: 'mydeposits', label: 'MyDeposits' },
  { value: 'other', label: 'Other' },
]

export const DEPOSIT_TYPES: { value: DepositType; label: string }[] = [
  { value: 'custodial', label: 'Custodial' },
  { value: 'insured', label: 'Insured' },
]

export const TENANT_DOCUMENT_TYPES: { value: TenantDocumentType; label: string; required: boolean; requiredFor?: 'all' | 'pre_rra' | 'post_rra' }[] = [
  { value: 'gas_safety', label: 'Gas Safety Certificate', required: true, requiredFor: 'all' },
  { value: 'eicr', label: 'EICR', required: true, requiredFor: 'all' },
  { value: 'epc', label: 'EPC', required: true, requiredFor: 'all' },
  { value: 'deposit_cert', label: 'Deposit Protection Certificate', required: true, requiredFor: 'all' },
  { value: 'inventory', label: 'Inventory', required: false },
  { value: 'ast', label: 'Tenancy Agreement', required: false },
  { value: 'legionella', label: 'Legionella Risk Assessment', required: false },
  { value: 'hmo_licence', label: 'HMO Licence', required: false },
  { value: 'buildings_insurance', label: 'Buildings Insurance', required: false },
  { value: 'landlord_insurance', label: 'Landlord Insurance', required: false },
  { value: 'fire_safety', label: 'Fire Safety', required: false },
  { value: 'pat_testing', label: 'PAT Testing', required: false },
  { value: 'other', label: 'Other', required: false },
  { value: 'written_statement_of_terms', label: 'Written Statement of Terms', required: true, requiredFor: 'post_rra' },
  { value: 'government_information_sheet', label: 'Government Information Sheet', required: true, requiredFor: 'all' },
  { value: 'prescribed_information', label: 'Prescribed Information (Deposit)', required: true, requiredFor: 'all' },
]

/**
 * Get required document types for a tenancy based on its RRA status.
 * All tenancies need the Government Information Sheet.
 * Post-RRA tenancies also need the Written Statement of Terms.
 */
export function getRequiredDocumentTypes(isPreRra: boolean): TenantDocumentType[] {
  return TENANT_DOCUMENT_TYPES
    .filter(t => {
      if (!t.required) return false
      if (!t.requiredFor || t.requiredFor === 'all') return true
      if (t.requiredFor === 'pre_rra') return isPreRra
      if (t.requiredFor === 'post_rra') return !isPreRra
      return false
    })
    .map(t => t.value)
}

// ============================================================
// TENANT ONBOARDING TYPES
// ============================================================

export type OnboardingStepKey =
  | 'agreement_review'
  | 'deposit_info'
  | 'document_acknowledgement'
  | 'property_info'
  | 'emergency_contacts'
  | 'rent_setup'

export interface TenantOnboardingStep {
  id: string
  tenancy_id: string
  tenant_id: string
  step_key: OnboardingStepKey
  label: string
  description: string | null
  display_order: number
  is_required: boolean
  completed_at: string | null
  completed_by: string | null
  created_at: string
  updated_at: string
}

export interface TenantOnboardingProgress {
  total: number
  completed: number
  percentage: number
  steps: TenantOnboardingStep[]
}

export const TENANT_ONBOARDING_STEPS: { key: OnboardingStepKey; label: string; description: string; required: boolean; order: number }[] = [
  { key: 'agreement_review', label: 'Review Tenancy Agreement', description: 'Read and acknowledge your tenancy agreement', required: true, order: 1 },
  { key: 'deposit_info', label: 'Deposit Protection', description: 'Review your deposit protection details', required: true, order: 2 },
  { key: 'document_acknowledgement', label: 'Acknowledge Documents', description: 'Review and acknowledge required documents', required: true, order: 3 },
  { key: 'property_info', label: 'Property Information', description: 'Review property details, Wi-Fi, meters, and bins', required: true, order: 4 },
  { key: 'emergency_contacts', label: 'Emergency Contacts', description: 'Save emergency contact numbers for your property', required: true, order: 5 },
  { key: 'rent_setup', label: 'Rent Setup', description: 'Confirm your rent payment method and first payment date', required: true, order: 6 },
]
